/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import LoadingOverlay from 'react-loading-overlay-ts';
import { Button, Paper } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { ViewHeader } from '@components/ViewHeader';
import MapView, { mapStyles } from '@components/MapView/MapView';
import { MAP_LAYERS } from '@components/FieldMapOptionsModal/FieldMapOptionsModal';
import { useAppTheme } from '@hooks/useAppTheme';
import { useGetFieldTables } from '@hooks/useGetFieldTables';
import { useGetCurrentLocation } from '@hooks/useGetCurrentLocation';
import { useFieldMapOptionsModal } from '@hooks/modals/useFieldMapOptionsModal';
import { RootState } from '@state-mgmt/store';
import { styles } from './styles';
import { selectFieldVisitWizardMapStyle } from '@state-mgmt/slices/visitSlice';
import { useGetFieldVisitsSteps } from '@hooks/useGetFieldVisitsSteps';
import usePreviousVisitMarkers from '@components/PreviousVisitMarkers/usePreviousVisitMarkers';
import useImagery from '@hooks/useImagey';
import { useGetSoilTable } from '@hooks/useGetSoilTable';
import { useGetFieldNotes } from '@hooks/useGetFieldNotes';
import { useFieldNotesMarkers } from '@hooks/useFieldNoteMarkers';
import './styles.css';
import useFetchClientFieldVisits from '@hooks/useFetchClientFieldVisits';

const FieldMap = () => {
  const { t } = useTranslation();

  const { fieldId, protocolId } = useParams();
  const theme = useAppTheme({});
  const [fieldBoundary, setFieldBoundary] = useState<any | undefined>();
  const [trialAreaGeoJson, setTrialAreaGeoJson] = useState<any | undefined>();
  const [zoneGeojson, setZoneGeojson] = useState<any | undefined>();
  const [soilGeojson, setSoilGeojson] = useState<any | undefined>();
  const [getFieldTablesState, getFieldTables] = useGetFieldTables();
  const [, getSoilTable] = useGetSoilTable();
  const [getFieldVisitsStepsState, getFieldVisitsSteps] = useGetFieldVisitsSteps();

  const mapStyle = useSelector(selectFieldVisitWizardMapStyle);

  const [treatmentZoneLegends, setTreatmentZoneLegends] = useState<{ label: string; color: string }[]>([]);
  const getCurrentLocation = useGetCurrentLocation();
  const { trialId, clientId } = useSelector((state: RootState) => state.app);
  const { trialProducts } = useSelector((state: RootState) => state.trial);

  const [getFieldNotesState] = useGetFieldNotes((fieldId || null) as string | null, protocolId as string | null, trialId || null);
  const fieldNotesMarkers = useFieldNotesMarkers(getFieldNotesState.data, 'fieldNotePopup');
  useEffect(() => {
    getCurrentLocation();

    if (trialId && protocolId && fieldId) {
      getFieldTables(trialId, protocolId, fieldId, true, false);
      getFieldVisitsSteps(trialId, protocolId, fieldId, undefined, true);
    }
  }, [fieldId, protocolId, fieldId]);

  const { previousVisitsMarkers, handleOnPreviousMarkerLocationClicked } = usePreviousVisitMarkers({
    visits: getFieldVisitsStepsState.data,
    stepId: null,
    trialId: trialId || '',
    fieldId: fieldId || '',
    protocolId: protocolId || ''
  });

  useFetchClientFieldVisits({
    clientId: clientId,
    fieldId: fieldId,
    trialId: trialId,
    protocolId: protocolId
  });

  useEffect(() => {
    if (getFieldTablesState.data) {
      if (getFieldTablesState.data.field) {
        setFieldBoundary(getFieldTablesState.data.field);
      }
      if (getFieldTablesState.data.area) {
        setTrialAreaGeoJson(getFieldTablesState.data.area);
      }

      if (getFieldTablesState.data.treatmentsZones) {
        let treatmentZones: { label: string; productId: string; color: string }[] = [];
        getFieldTablesState.data.treatmentsZones.features.forEach((treatmentZone: any) => {
          const zoneProductId = treatmentZone.properties.product_id;
          if (zoneProductId && !treatmentZones.some(tz => tz.productId === zoneProductId)) {
            const product = trialProducts.find(trialProduct => trialProduct.id === zoneProductId);
            if (product) {
              treatmentZones = [...treatmentZones, { label: product.name, color: product.color, productId: product.id }];
            }
          }
        });
        setTreatmentZoneLegends(treatmentZones);
        setZoneGeojson(getFieldTablesState.data.treatmentsZones);
      }

      if (getFieldTablesState.data.soil) {
        const data = getFieldTablesState.data.soil;
        setSoilGeojson(data);
      } else if (getFieldTablesState.data.boundaryTableGuid) {
        getSoilTable(getFieldTablesState.data.boundaryTableGuid).then(response => {
          setSoilGeojson(response.soil);
        });
      }
    }
  }, [getFieldTablesState.data, trialProducts]);

  const { selectedImage } = useImagery({ fieldId, clientId, trialId, protocolId });

  const {
    displayModal,
    selectedLayers,
    display: mapOptionsModalDisplay,
    drawerBottomInset
  } = useFieldMapOptionsModal({
    treatmentZones: treatmentZoneLegends,
    hasTreatmentZone: !!zoneGeojson,
    trialId: trialId || '',
    protocolId: protocolId || '',
    displayVisitTrialSelectWithSingleOption: true,
    adjustMapForDrawer: true
  });

  return (
    <Paper css={styles.container(theme)}>
      <ViewHeader title={t('field-map.title')} />
      <Button css={styles.mapOptionBtn} variant="text" startIcon={<TuneIcon />} disabled={getFieldTablesState.isLoading} onClick={() => displayModal()}>
        {t('field-map.options-button')}
      </Button>
      <div css={styles.mapContainer}>
        <LoadingOverlay
          spinner
          active={getFieldTablesState.isLoading || getFieldVisitsStepsState.isLoading}
          styles={{ wrapper: styles.mapLoadingOverlayWrapper }}
          text={t('field-map.loading-field')}
        >
          <MapView
            previousVisitsMarkers={previousVisitsMarkers}
            onPreviousMarkerLocationClicked={handleOnPreviousMarkerLocationClicked}
            mapStyle={mapStyle || mapStyles.satellite}
            fieldBoundary={fieldBoundary}
            bottomInset={drawerBottomInset}
            showCurrentPosition={true}
            showFlyToCurrentLocation={true}
            showSsurgo={selectedLayers.includes(MAP_LAYERS.SURGO)}
            showTreatmentZones={selectedLayers.includes(MAP_LAYERS.TREATMENT_ZONES)}
            showTrialArea={selectedLayers.includes(MAP_LAYERS.TRIAL_AREA)}
            soilGeojson={soilGeojson}
            trialAreaGeoJson={trialAreaGeoJson}
            zoneGeojson={zoneGeojson}
            selectedImage={selectedImage}
            fieldNotesMarkers={selectedLayers.includes(MAP_LAYERS.FIELD_NOTES) ? fieldNotesMarkers : []}
          />
        </LoadingOverlay>
      </div>
      {mapOptionsModalDisplay}
    </Paper>
  );
};

export default FieldMap;
